/**
 * Portal strings (English). Registered with `registerMessages("en", en)` in main.tsx; the shared status language,
 * risk, source and conflict strings come from @iga/ui. Copy follows HANDOFF §7: plain, names people, says what
 * happens next; sentence case; buttons are verb + object. Every string is designed for +30 % length (Danish).
 */
export const en: Record<string, string> = {
  "p.you": "You",

  // ---- navigation and shell
  "p.nav.home": "Home",
  "p.nav.tasks": "Tasks",
  "p.nav.request": "Request access",
  "p.nav.requestShort": "Request",
  "p.nav.myAccess": "My access",
  "p.nav.myRequests": "My requests",
  "p.nav.reviews": "Access reviews",
  "p.nav.team": "My team",
  "p.nav.servicedesk": "Service desk",
  "p.nav.profile": "Profile & delegation",
  "p.menu.account": "Account menu for {name}",
  "p.menu.language": "Language",

  // ---- shared
  "p.people.placeholder": "Search by name or e-mail",
  "p.people.noMatches": "Nobody matches that",
  "p.people.minChars": "Type at least 2 characters",
  "p.error.badge": "Couldn't load",
  "p.error.loadTitle": "This page couldn't be loaded",
  "p.error.loadTodo": "Try again in a moment. If it keeps happening, tell IT support what you were doing.",
  "p.error.retry": "Try again",
  "p.noIdentity.title": "Your account isn't linked to a person yet",
  "p.noIdentity.body": "My access, My requests and My team need that link. Approvals and the service desk work without it. Ask IT support to link your account.",
  "p.noIdentity.short": "Not available until your account is linked to a person.",
  "p.sod.otherSide": "the other side of the policy “{policy}”",
  "p.action.grant": "New access",
  "p.action.revoke": "Removal",
  "p.action.extend": "Extension",
  "p.approverKind.manager": "manager",
  "p.approverKind.role_owner": "role owner",
  "p.approverKind.system_owner": "system owner",
  "p.approverKind.entitlement_owner": "owner",
  "p.approverKind.user": "approver",
  "p.approverKind.group": "group",

  // ---- home
  "p.home.morning": "Good morning, {name}",
  "p.home.afternoon": "Good afternoon, {name}",
  "p.home.evening": "Good evening, {name}",
  "p.home.nothingWaiting": "Nothing is waiting for you",
  "p.home.nothingWaitingBody": "Approvals, access reviews and requests that need you will show up here.",
  "p.home.oneThing": "1 thing needs you.",
  "p.home.things": "{count} things need you.",
  "p.home.todo": "What needs you",
  "p.home.allTasks": "See all tasks",
  "p.home.row.failed": "{count} of your requests couldn't be set up",
  "p.home.row.failedContext": "An admin has to look at why. See what happened.",
  "p.home.row.escalated": "{count} approvals were passed to you",
  "p.home.row.escalatedContext": "The original approver didn't decide in time.",
  "p.home.row.approval": "1 request waits for your decision",
  "p.home.row.approvals": "{count} requests wait for your decision",
  "p.home.row.approvalsDue": "{count} due within a day",
  "p.home.row.approvalsContext": "Approve or reject them in Tasks.",
  "p.home.row.review": "Access review: {name}",
  "p.home.row.reviewContext": "{left} items left · due {date}",
  "p.home.row.pending": "1 of your requests is on its way",
  "p.home.row.pendings": "{count} of your requests are on their way",
  "p.home.row.pendingContext": "You get an e-mail when they are decided.",
  "p.home.row.team": "{count} open requests for people in your team",
  "p.home.row.teamContext": "Waiting for approval.",
  "p.home.expiring": "Access that ends soon",
  "p.home.nothingExpires": "Nothing ends in the next 30 days.",
  "p.home.quick": "Shortcuts",
  "p.home.requestForMe": "Request access for me",
  "p.home.requestForTeam": "Request for someone in my team",
  "p.home.seeAccess": "See what I have access to",
  "p.home.setDelegation": "Set an out-of-office delegate",
  "p.home.counts": "You have {roles} roles, {entitlements} direct entitlements and {accounts} accounts.",

  // ---- tasks (inbox)
  "p.tasks.waitOne": "1 thing waits for your decision.",
  "p.tasks.wait": "{count} things wait for your decision.",
  "p.tasks.waitUrgent": "{count} things wait for your decision. {urgent} need attention now.",
  "p.tasks.healthy": "Nothing is blocked or overdue.",
  "p.tasks.emptyBody": "Approvals, access reviews and requests that need you will show up here.",
  "p.tasks.itemGone": "That item is no longer in your inbox.",
  "p.tasks.type.approval": "Approval",
  "p.tasks.type.review": "Access review",
  "p.tasks.type.request": "Request",
  "p.tasks.source.approvals": "Approvals",
  "p.tasks.source.reviews": "Access reviews",
  "p.tasks.source.requests": "Requests",
  "p.tasks.for": "for {name}",
  "p.tasks.askedBy": "asked by {name}",
  "p.tasks.askedSelf": "asked themselves",
  "p.tasks.due": "due {when}",
  "p.tasks.overdue": "overdue · was due {when}",
  "p.tasks.dueShort": "due {when}",
  "p.tasks.overdueShort": "overdue",
  "p.tasks.reviewContext": "{left} of {total} items left",
  "p.tasks.reviewLeft": "{left} items are left. The review is due {date}.",
  "p.tasks.openReview": "Open the review",
  "p.tasks.failedTitle": "Request #{n} couldn't be set up",
  "p.tasks.failedBody": "The decisions were made, but setting up the access for {name} failed in at least one system.",
  "p.tasks.failedFix": "An admin has to look at why. You don't need to do anything, but you can ask IT support for an update.",

  // ---- approval detail (J2)
  "p.approval.wants": "{name} wants {item}",
  "p.approval.request": "a request",
  "p.approval.asDelegate": "on behalf of {name}",
  "p.approval.askedSelf": "{name} asked for it {when}",
  "p.approval.askedBy": "Asked by {by} on {when}",
  "p.approval.in": "in {system}",
  "p.approval.lets": "What it lets {name} do",
  "p.approval.noDescription": "No description was given for this access.",
  "p.approval.why": "Why, in {name}'s words",
  "p.approval.noJustification": "No reason was given.",
  "p.approval.until": "Until",
  "p.approval.noEndDate": "No end date",
  "p.approval.action": "Type",
  "p.approval.conflictTitle": "Conflict with access {name} already has",
  "p.approval.conflictBody": "The policy allows it, but the conflict is recorded and the policy owner is told. Say in the comment why it is acceptable, or reject.",
  "p.approval.steps": "Who decides",
  "p.approval.step": "Step {n}",
  "p.approval.group": "Anyone in {group}",
  "p.approval.unassigned": "Nobody yet",
  "p.approval.skippedSelf": "asked for it themselves",
  "p.approval.comment": "Comment",
  "p.approval.commentOptional": "Comment (optional)",
  "p.approval.commentHint": "Required to reject. The requester reads it.",
  "p.approval.commentHintConflict": "Required to reject, recommended when approving a conflict. The requester reads it.",
  "p.approval.reason": "Reason",
  "p.approval.reasonRequired": "Say why you are rejecting. The requester reads this.",
  "p.approval.approve": "Approve",
  "p.approval.reject": "Reject",
  "p.approval.seeAccess": "See all of {name}'s access",
  "p.approval.openRequest": "Open the request",
  "p.approval.approvedToast": "Approved {item} for {name}",
  "p.approval.rejectedToast": "Rejected {item} for {name}",

  // ---- bulk approval
  "p.bulk.selectMode": "Select several",
  "p.bulk.select": "Select {item}",
  "p.bulk.selected": "{count} selected",
  "p.bulk.guard": "Only low-risk items without conflicts can be decided together. Open the others one by one.",
  "p.bulk.approveN": "Approve {count}",
  "p.bulk.rejectN": "Reject {count}",
  "p.bulk.approveTitle": "Approve {count} requests?",
  "p.bulk.rejectTitle": "Reject {count} requests?",
  "p.bulk.openToDecide": "Open to decide",
  "p.bulk.someNotDecided": "Some items need you to open them before you decide.",
  "p.bulk.approved": "Approved",
  "p.bulk.rejected": "Rejected",
  "p.bulk.approvedToast": "Approved {count} requests",
  "p.bulk.rejectedToast": "Rejected {count} requests",

  // ---- request access (J1)
  "p.request.subtitle": "Find what you or someone in your team needs. We ask the right people to approve it.",
  "p.request.for": "For",
  "p.request.forMe": "Me",
  "p.request.forTeam": "Someone in my team",
  "p.request.forAnyone": "Someone else",
  "p.request.change": "Change",
  "p.request.pickPerson": "Who needs access?",
  "p.request.chooseFirst": "Choose who needs access, then search.",
  "p.request.searchPlaceholder": "Search for a role, an application or what you need to do",
  "p.request.applications": "Applications",
  "p.request.allApplications": "All applications",
  "p.request.browseAll": "Browse all {count}",
  "p.request.fewerApplications": "Fewer applications",
  "p.request.results": "{count} results",
  "p.request.noResults": "Nothing matches",
  "p.request.noResultsHint": "Try another word, or clear the application filter.",
  "p.request.nothingPublished": "No access can be requested for this person yet.",
  "p.request.noIdentity": "Your account isn't linked to a person, so you can't request access for yourself. IT support can request on your behalf.",
  "p.request.inRequest": "{count} in your request",
  "p.request.review": "Review request",
  "p.request.panelTitle": "Request for {name}",
  "p.request.forMeTitle": "you",
  "p.request.panelEmptyTitle": "Your request",
  "p.request.panelEmptyBody": "Pick something from the results. You explain why and send it from here.",
  "p.request.until": "Until",
  "p.request.roleDefault": "Role default · {days} days",
  "p.request.untilDate": "A date I choose",
  "p.request.why": "Why does {name} need this?",
  "p.request.whyMe": "Why do you need this?",
  "p.request.whyHint": "Approvers read this. Say what the work is, not just “needed for job”.",
  "p.request.justificationRequired": "Say why the access is needed. Approvers read this.",
  "p.request.whoDecides": "Who decides",
  "p.request.previewUnavailable": "Shown after sending.",
  "p.request.autoApprove": "No approval needed. The access is set up right away.",
  "p.request.wouldBeBlocked": "Can't be requested because of the policy above.",
  "p.request.skippedSelf": "skipped – you asked",
  "p.request.dueWithin": "Decision due within {days} days.",
  "p.request.emailMe": "You get an e-mail either way.",
  "p.request.emailBoth": "You and {name} get an e-mail either way.",
  "p.request.send": "Send request",
  "p.request.addAnother": "Add another item",
  "p.request.sent": "Request sent",
  "p.request.sentTo": "Request sent to {name}",
  "p.request.sendFailed": "The request couldn't be sent",

  // ---- catalogue rows
  "p.catalog.request": "Request",
  "p.catalog.requestItem": "Request {item}",
  "p.catalog.inRequest": "In your request",
  "p.catalog.removeFrom": "Remove {item} from the request",
  "p.catalog.youHave": "You have this",
  "p.catalog.has": "{name} has this",
  "p.catalog.alreadyRequested": "Already requested",
  "p.catalog.seeRequest": "See the request",
  "p.catalog.detectHint": "It can still be requested. Approvers will see the conflict.",
  "p.catalog.blockedHint": "{name} would first need to give that up. Ask IT support if this is wrong.",

  // ---- my requests and the request page
  "p.requests.subtitle": "Requests you sent, and requests sent for you.",
  "p.requests.one": "Request",
  "p.requests.number": "Request #{n}",
  "p.requests.notFound": "This request doesn't exist or isn't yours to see.",
  "p.requests.col.access": "Access",
  "p.requests.col.for": "For",
  "p.requests.col.status": "Status",
  "p.requests.col.sent": "Sent",
  "p.requests.search": "Search by access or request number",
  "p.requests.allStates": "All",
  "p.requests.approval": "approval",
  "p.requests.scope": "Show",
  "p.requests.mine": "Mine",
  "p.requests.team": "My team's",
  "p.requests.emptyTitle": "You haven't requested anything yet",
  "p.requests.emptyBody": "Search the catalogue, say why you need it, and the right people are asked to approve.",
  "p.requests.askedBy": "Asked by",
  "p.requests.why": "Why",
  "p.requests.waiting": "{name} has it.",
  "p.requests.waitingUntil": "{name} has it. They have until {date}.",
  "p.requests.beingSetUp": "Everyone said yes. The access is being set up now.",
  "p.requests.ready": "The access is in place.",
  "p.requests.rejectedBy": "{name} said no. The reason is below.",
  "p.requests.cancel": "Cancel request",
  "p.requests.keep": "Keep request",
  "p.requests.cancelTitle": "Cancel this request?",
  "p.requests.cancelBody": "Request #{n} is withdrawn and the approvers no longer see it. You can send a new one any time.",
  "p.requests.cancelled": "Request #{n} cancelled",
  "p.requests.step.sent": "Sent",
  "p.requests.step.sentDetail": "{when} by {by}",
  "p.requests.step.due": "Due {date}",
  "p.requests.step.decided": "{when} by {by}",
  "p.requests.step.skippedYou": "Skipped – you asked for it",
  "p.requests.step.skippedSelf": "Skipped – they asked for it themselves",
  "p.requests.step.noApproval": "No approval needed",
  "p.requests.step.ready": "Access ready",
  "p.requests.step.willBeReady": "Access ready",
  "p.requests.step.autoAfter": "Set up automatically after approval",
  "p.requests.step.failed": "Couldn't be set up",
  "p.requests.step.notSetUp": "Not set up",

  // ---- my access
  "p.access.subtitle": "Roles, entitlements and accounts you have right now.",
  "p.access.roles": "Roles",
  "p.access.entitlements": "Entitlements",
  "p.access.accounts": "Accounts",
  "p.access.otherRoles": "Other roles",
  "p.access.noRoles": "You have no roles yet",
  "p.access.noRolesBody": "Roles you request, or get automatically from your job, show up here.",
  "p.access.noEntitlements": "No direct entitlements",
  "p.access.noEntitlementsBody": "Access that comes with your roles is shown under Accounts.",
  "p.access.noAccounts": "No accounts yet",
  "p.access.noAccountsBody": "Accounts are created in the systems when access is set up.",
  "p.access.noAccountEntitlements": "Nothing on this account yet.",
  "p.access.lastRead": "read {when}",
  "p.access.viaPlatform": "Through the platform",
  "p.access.noEndDate": "No end date",
  "p.access.until": "until {date}",
  "p.access.extend": "Extend",
  "p.access.remove": "Remove",
  "p.access.extendTitle": "Keep {item} longer",
  "p.access.currentEnd": "Ends {date}.",
  "p.access.newEnd": "New end date",
  "p.access.whyStill": "Why do you still need it?",
  "p.access.extendSent": "Extension request sent",
  "p.access.expiringTitle": "{count} of your accesses end within 30 days",
  "p.access.expiringBody": "Use Extend on the row to keep it. Otherwise it is removed on the end date.",
  "p.access.conflictsTitle": "{count} of your accesses conflict with each other",
  "p.access.conflictsBody": "The policy owner has been told. You don't need to do anything unless they contact you.",

  // ---- access review (J3)
  "p.review.listSubtitle": "Check that people still need what they have.",
  "p.review.none": "No reviews waiting for you",
  "p.review.noneBody": "You are told by e-mail when a review needs you.",
  "p.review.type.manager": "Your team",
  "p.review.type.role_owner": "Roles you own",
  "p.review.type.system_owner": "Systems you own",
  "p.review.type.entitlement_owner": "Entitlements you own",
  "p.review.asDelegate": "as delegate",
  "p.review.progress": "{decided} of {total} decided",
  "p.review.left": "{count} left",
  "p.review.continue": "Continue the review",
  "p.review.view": "See decisions",
  "p.review.title": "Access review: {name}",
  "p.review.purpose": "Check that each person still needs what they have.",
  "p.review.dueSentence": "Due {date}.",
  "p.review.removedAtClose": "Access you revoke is removed when the review closes.",
  "p.review.noItems": "Nothing to review",
  "p.review.noItemsBody": "There are no items for you in this review.",
  "p.review.people": "People",
  "p.review.yourTeam": "Your team · {count} people",
  "p.review.peopleCount": "{count} people",
  "p.review.pickPerson": "Pick a person to see their access.",
  "p.review.done": "Done",
  "p.review.keepLowRisk": "Keep {count} low-risk automatic items",
  "p.review.keepN": "Keep {count} items",
  "p.review.decideOneByOne": "Decide one by one",
  "p.review.bulkBody": "Access {name} gets automatically from the job, with low or medium risk and no conflict, is marked as kept. You can change any of them afterwards.",
  "p.review.bulkKept": "Kept {count} items",
  "p.review.bulkKeptPartial": "Kept {count} items · {failed} couldn't be decided",
  "p.review.conflict": "Conflict · {count}",
  "p.review.conflictNote": "{name} has {count} open conflicts between accesses. Look at the high-risk items first.",
  "p.review.account": "Account",
  "p.review.entitlements": "{count} entitlements",
  "p.review.decisionFor": "Decision for {item}",
  "p.review.keep": "Keep",
  "p.review.revoke": "Revoke",
  "p.review.passOn": "Pass on",
  "p.review.assignOwner": "Assign owner",
  "p.review.kept": "Kept {item}",
  "p.review.revoked": "Marked {item} for removal",
  "p.review.passedOn": "Passed on to {name}",
  "p.review.ownerAssigned": "{name} is proposed as owner",
  "p.review.ownerLabel": "Account owner",
  "p.review.ownerHint": "The account is linked to this person.",
  "p.review.reviewerLabel": "New reviewer",
  "p.review.reviewerHint": "The item moves to this person's tasks.",
  "p.review.personDone": "{name} is done",
  "p.review.summary": "{kept} kept, {revoked} revoked, {passed} passed on.",
  "p.review.removedWhen": "Removed when the review closes, {date}",
  "p.review.canChange": "You can change any decision until the review closes.",
  "p.review.personProgress": "{decided} of {total} decided for {name}",
  "p.review.savedAsYouGo": "Decisions save as you go",
  "p.review.next": "Next: {name} · {left} left",
  "p.review.allDone": "All decided – back to Tasks",

  // ---- my team
  "p.team.subtitle": "The people who report to you, their access and what needs your attention.",
  "p.team.none": "Nobody reports to you",
  "p.team.col.person": "Person",
  "p.team.col.access": "Access",
  "p.team.col.attention": "Needs attention",
  "p.team.access": "{roles} roles · {accounts} accounts",
  "p.team.pending": "{count} requests on the way",
  "p.team.toReview": "{count} to review",
  "p.team.disabled": "{count} accounts disabled",
  "p.team.violationsTitle": "{name} has {count} open conflicts between accesses",
  "p.team.pendingTitle": "{count} requests for {name} are on their way",
  "p.team.seeRequests": "See the requests",
  "p.team.removeTitle": "Remove {item}?",
  "p.team.keepAccess": "Keep access",
  "p.team.removeBody": "{item} is removed from {name} right away. It is recorded as a request you approved.",
  "p.team.removed": "Removed {item} from {name}",

  // ---- service desk (J6)
  "p.sd.subtitle": "Find a person to unlock accounts, reset passwords or request access for them.",
  "p.sd.searchPlaceholder": "Name, e-mail, username or employee ID",
  "p.sd.searchHint": "Enough detail to pick the right person: the employee ID is unique.",
  "p.sd.person": "1 person",
  "p.sd.people": "{count} people",
  "p.sd.noMatch": "Nobody matches that",
  "p.sd.noMatchBody": "Try the employee ID or the e-mail address.",
  "p.sd.notFound": "This person doesn't exist or has left.",
  "p.sd.accounts": "Accounts",
  "p.sd.noAccounts": "No accounts in any system.",
  "p.sd.col.account": "Account",
  "p.sd.col.status": "Status",
  "p.sd.col.lastRead": "Last read from system",
  "p.sd.col.actions": "Actions",
  "p.sd.noActions": "No actions",
  "p.sd.changePending": "Change on its way",
  "p.sd.unlock": "Unlock",
  "p.sd.reset": "Reset password",
  "p.sd.verifyFirst": "Confirm who you are talking to first",
  "p.sd.beforeTitle": "Before you change anything",
  "p.sd.verified": "I checked who I'm talking to",
  "p.sd.verifyHint": "Ask for the employee ID ({id}) and the manager's name ({manager}).",
  "p.sd.noResetPermission": "Your role can look, but not unlock or reset. Ask an admin.",
  "p.sd.otherHelp": "Other help",
  "p.sd.requestFor": "Request access for {name}",
  "p.sd.seeAccess": "See {name}'s access",
  "p.sd.hideAccess": "Hide {name}'s access",
  "p.sd.accessOf": "{name}'s access",
  "p.sd.portalLogin": "Portal login",
  "p.sd.loginDisabled": "Disabled",
  "p.sd.lastLogin": "Last signed in {when}",
  "p.sd.neverLoggedIn": "Never signed in",
  "p.sd.noLogin": "No portal login",
  "p.sd.recentRequests": "Recent requests",
  "p.sd.resetTitle": "Reset password in {system}",
  "p.sd.resetBody": "A new password is made for {account}. You read it to {name}; it is shown once and not stored.",
  "p.sd.mustChange": "Must choose a new password at next sign-in",
  "p.sd.alsoUnlock": "Also unlock the account",
  "p.sd.resetConfirm": "Reset password",
  "p.sd.resetFailed": "The password couldn't be reset",
  "p.sd.newPasswordFor": "New password for {account}",
  "p.sd.done": "Done · {time}",
  "p.sd.stage.queued": "Queued",
  "p.sd.stage.sent": "Sent to {system}",
  "p.sd.stage.confirmed": "Confirmed by {system}",
  "p.sd.stageShort.queued": "Queued",
  "p.sd.stageShort.sent": "Sent to {system}",
  "p.sd.stageShort.confirmed": "Done in {system} · {time}",
  "p.sd.stageShort.retrying": "Retrying",
  "p.sd.stageShort.failed": "Failed",
  "p.sd.stageShort.cancelled": "Cancelled",
  "p.sd.retrying": "Attempt {attempts} failed – the platform tries again at {next}. Last error: {error}",
  "p.sd.failedAction": "The platform gave up. Last error: {error}. The administrators have been notified.",
  "p.sd.nextTry": "next try {next}",
  "p.sd.recentActions": "Recent actions (24 hours)",
  "p.sd.op.set_password": "Password reset",
  "p.sd.op.enable": "Unlock / enable",
  "p.sd.op.disable": "Disable",
  "p.sd.op.delete": "Delete",
  "p.sd.op.create_account": "Create account",
  "p.sd.op.update_account": "Update account",
  "p.sd.op.add_entitlement": "Grant access",
  "p.sd.op.remove_entitlement": "Remove access",
  "p.sd.unlocked": "Account unlocked",
  "p.sd.mustChangeNote": "{name} must choose a new password when signing in",
  "p.sd.noChangeNote": "{name} can keep using this password",
  "p.sd.shownOnce": "Shown once",
  "p.sd.shownOnceShort": "The password is shown once and is not stored.",
  "p.sd.shownOnceBody": "It is not stored and will not appear in the audit log. Read it to {name} now, or send it through a channel they trust.",
  "p.sd.givenTo": "I've given it to {name}",
  "p.sd.handedOver": "Password handed to {name}. It is gone from this screen.",
  "p.sd.unlockTitle": "Unlock {account}?",
  "p.sd.keepLocked": "Keep it locked",
  "p.sd.unlockBody": "The account in {system} is unlocked. {name} can sign in again with the password they already have.",
  "p.sd.unlockQueued": "Unlock on its way for {name}",

  // ---- profile & delegation
  "p.profile.me": "About you",
  "p.profile.none": "No profile",
  "p.profile.name": "Name",
  "p.profile.email": "E-mail",
  "p.profile.userName": "Username",
  "p.profile.employeeId": "Employee ID",
  "p.profile.type": "Type",
  "p.profile.jobTitle": "Job title",
  "p.profile.department": "Department",
  "p.profile.manager": "Manager",
  "p.profile.orgUnit": "Unit",
  "p.profile.directReports": "People reporting to you",
  "p.profile.validTo": "Employed until",
  "p.profile.delegation": "Out-of-office delegation",
  "p.profile.delegationBody": "While you are away, someone else can decide your approvals and access reviews.",
  "p.profile.newDelegation": "Add a delegate",
  "p.profile.iDelegateTo": "I delegate to",
  "p.profile.delegatedToMe": "Delegated to me",
  "p.profile.noDelegations": "No delegates right now.",
  "p.profile.nobodyDelegated": "Nobody has delegated to you.",
  "p.profile.untilRemoved": "until removed",
  "p.profile.active": "Active",
  "p.profile.scheduled": "Starts later",
  "p.profile.ended": "Ended",
  "p.profile.remove": "Remove",
  "p.profile.delegateTo": "Delegate to",
  "p.profile.scopeLabel": "What they may decide",
  "p.profile.scope.all": "Approvals and access reviews",
  "p.profile.scope.approvals": "Approvals only",
  "p.profile.scope.certifications": "Access reviews only",
  "p.profile.from": "From",
  "p.profile.until": "Until",
  "p.profile.untilHint": "Leave empty for open-ended",
  "p.profile.createDelegation": "Add delegate",
  "p.profile.delegated": "{name} can now decide for you",
  "p.profile.removeTitle": "Remove this delegate?",
  "p.profile.keepDelegation": "Keep delegate",
  "p.profile.removeBody": "{name} can no longer decide on your behalf.",
  "p.profile.removedToast": "Delegate removed",
  "p.profile.changePassword": "Change password",
  "p.profile.mustChange": "Set a new password before you continue.",
  "p.profile.currentPw": "Current password",
  "p.profile.newPw": "New password",
  "p.profile.pwHint": "At least 12 characters with a digit and an upper-case letter",
  "p.profile.confirmPw": "Repeat the new password",
  "p.profile.pwMismatch": "The two passwords are not the same.",
  "p.profile.pwChanged": "Password changed",
};
